import { TranslationKey } from '../../../../types/translation/TranslationKey';
import { useContext, useMemo, useState } from 'react';
import { ProductMasterDataContext } from '../../../../context/ProductMasterDataContext';
import React from 'react';
import { translate } from '../../../../types/translation/Translator';
import { LotInput } from '../../../../types/lot/lot.input';
import DatePicker from '../../../components/DatePicker/DatePicker';
import { Lot } from '../../../../types/lot';
import { CustomFieldContext } from '../../../../context/CustomFieldContext';
import { CustomFieldEntityType } from '../../../../types/custom_field';
import { CompanyContext } from '../../../../context/CompanyContext';
import { DropdownSearchSelect } from '../../../components/Dropdown';
import ProductMasterDataDropdownItem from '../../../components/Dropdown/templates/ProductMasterData/ProductMasterDataDropdownItem';
import { ProductMasterData } from '../../../../types/product_master_data/productMasterData';
import CustomFieldInput from '../../Common/CustomFieldInput';
import { TextInput } from '../../../components/TextInput';
import { ActionModalPane } from '../../../components/Modal';
import { CancelButton, CreateButton } from '../../../components/Button';
import { CreateLotResponse, CreateLotVariables, LotMutations } from '../../../../graphql/lot.graphql';
import { LotContext } from '../../../../context/LotContext';
import { useToast } from '../../../../providers/ToastProvider';
import { ToastType } from '../../../components/Toast';
import useMutation from '../../../../hooks/useMutation';
import { UserContext } from '../../../../context/UserContext';
import { ContactContext } from '../../../../context/ContactContext';

interface LotInfoModalPaneProps {
  pmdId?: string;
  onCreated?: (lot: Lot) => void;
  setOpen: (open: boolean) => void;
}

export default function LotInfoModalPane({ pmdId, onCreated, setOpen }: LotInfoModalPaneProps) {
  const { showToast } = useToast();

  const { currentCompany } = useContext(CompanyContext);
  const { productMasterData } = useContext(ProductMasterDataContext);
  const { customFields } = useContext(CustomFieldContext);
  const { lots, setLots } = useContext(LotContext);
  const { companyUsers } = useContext(UserContext);
  const { contacts } = useContext(ContactContext);

  const [input, setInput] = useState<LotInput>(
    new LotInput({ companyId: currentCompany.id, productMasterDataId: pmdId || '' }),
  );

  const pmds = useMemo(() => {
    return [...productMasterData.values()].filter(pmd => pmd.lotManaged && pmd.companyId === currentCompany.id);
  }, [productMasterData]);

  const lotCustomFields = useMemo(() => {
    return [...customFields.values()].filter(
      cf =>
        cf.companyId === currentCompany.id &&
        cf.entityType === CustomFieldEntityType.lot &&
        !cf.generated &&
        !cf.hidden,
    );
  }, [customFields]);

  const [create, { loading }] = useMutation<CreateLotResponse, CreateLotVariables>(LotMutations.create, {
    onCompleted: response => {
      const createdLot = response.createLot[0];
      lots.set(createdLot.id, new Lot(createdLot));
      setLots(new Map(lots));
      showToast({
        title: translate({ key: TranslationKey.lotCreatedSuccessfullyToastTitle }),
        type: ToastType.success,
      });
      if (onCreated) onCreated(createdLot);
      setOpen(false);
    },
  });

  const handleCreate = async () => {
    await create({
      variables: {
        lots: [input.forCreate()],
      },
    });
  };

  const footer = (
    <div className='flex justify-end gap-2'>
      <CancelButton onClick={() => setOpen(false)} disabled={loading} />
      <CreateButton disabled={!input.validate(customFields)} onClick={handleCreate} loading={loading} />
    </div>
  );

  return (
    <ActionModalPane footer={footer}>
      <div className='grid gap-4'>
        <div className='grid grid-cols-2 gap-4'>
          <DropdownSearchSelect<ProductMasterData>
            mandatory
            label={translate({ key: TranslationKey.productReferenceData })}
            placeholder={translate({ key: TranslationKey.selectProductReference })}
            items={pmds}
            value={productMasterData.get(input.productMasterDataId)}
            textBuilder={p => `${p.productName} ${p.productNumber}`}
            itemBuilder={p => <ProductMasterDataDropdownItem productMasterData={p} />}
            onSelect={p => setInput(input.withProductMasterDataId(p?.id))}
          />
          <TextInput
            mandatory
            label={translate({ key: TranslationKey.lotNumber })}
            placeholder={translate({ key: TranslationKey.lotNumber })}
            value={input.number}
            onChange={nr => setInput(input.withNumber(nr))}
          />
          <DatePicker
            label={translate({ key: TranslationKey.expirationDate })}
            placeholder={translate({ key: TranslationKey.expirationDate })}
            value={input.expirationDate}
            onChange={(val?: string) => setInput(input.withExpirationDate(val))}
          />
        </div>

        {lotCustomFields.length ? (
          <div className='grid gap-2'>
            <p className='section-title text-base'>{translate({ key: TranslationKey.customFields })}</p>
            <div className='grid grid-cols-2 gap-4'>
              {lotCustomFields.map(cf => (
                <div className='w-full'>
                  <CustomFieldInput
                    item={cf}
                    entity={input}
                    onChange={setInput}
                    value={input.customFields.get(cf.id)?.value}
                    users={companyUsers}
                    contacts={contacts}
                    productMasterData={productMasterData}
                  />
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </ActionModalPane>
  );
}
